/**
 * Wishlist / Favourites
 * --------------------------------------------------------------------------
 * Guest friendly favourites list, stored in localStorage and mirrored into a
 * first-party cookie so that server side tooling (e-mail flows, app proxies,
 * analytics) can read the visitor's list without an app embed.
 *
 * When a customer is logged in the guest list is claimed by that customer and,
 * if `syncUrl` is configured in the theme settings, pushed to that endpoint so
 * it can be persisted on the account and read back through `customerItems`.
 *
 * Public API — window.Wishlist
 *   .all()               -> Array<Item>   newest first
 *   .has(handle)         -> Boolean
 *   .get(handle)         -> Item|null
 *   .add(item)           -> Boolean       true when it was added
 *   .remove(handle)      -> Boolean       true when it was removed
 *   .toggle(item)        -> Boolean       resulting active state
 *   .count()             -> Number
 *   .clear()             -> void
 *   .subscribe(callback) -> unsubscribe function
 *
 * Custom elements — <wishlist-button> <wishlist-count> <wishlist-list>
 * Events — `wishlist:change` on `document` with { items, count, handle, active }
 */
(function () {
  'use strict';

  /* ----------------------------------------------------------------------- */
  /* Configuration                                                           */
  /* ----------------------------------------------------------------------- */

  var config = Object.assign(
    {
      storageKey: 'theme:wishlist:v1',
      cookieName: 'wishlist',
      countCookieName: 'wishlist_count',
      cookieDays: 365,
      cookieMaxLength: 3800,
      maxItems: 100,
      url: '/pages/wishlist',
      customerId: null,
      customerItems: [],
      syncUrl: '',
      showToast: true,
      strings: {}
    },
    window.wishlistConfig || {}
  );

  var strings = Object.assign(
    {
      add: 'Add to wishlist',
      remove: 'Remove from wishlist',
      added: 'Added to your wishlist',
      removed: 'Removed from your wishlist',
      view: 'View wishlist',
      count: 'Wishlist'
    },
    config.strings || {}
  );

  config.strings = strings;
  window.wishlistConfig = config;

  var STORAGE_VERSION = 1;
  var ownerId = config.customerId ? String(config.customerId) : null;

  /* ----------------------------------------------------------------------- */
  /* Cookie helpers (self contained so load order never matters)             */
  /* ----------------------------------------------------------------------- */

  function writeCookie(name, value, days) {
    var expires = '';

    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }

    try {
      document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
    } catch (error) {
      /* cookies unavailable — the localStorage copy is still authoritative */
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Storage                                                                 */
  /* ----------------------------------------------------------------------- */

  function isValidItem(item) {
    return !!item && typeof item.handle === 'string' && item.handle.length > 0;
  }

  function normalizeItem(item) {
    return {
      handle: String(item.handle),
      productId: item.productId ? String(item.productId) : null,
      variantId: item.variantId ? String(item.variantId) : null,
      addedAt: Number(item.addedAt) || Date.now()
    };
  }

  function readPayload() {
    var fallback = { v: STORAGE_VERSION, owner: null, items: [] };

    try {
      var raw = window.localStorage.getItem(config.storageKey);
      if (!raw) return fallback;

      var parsed = JSON.parse(raw);

      // Tolerate a bare array from any earlier/simpler implementation.
      if (Array.isArray(parsed)) {
        return { v: STORAGE_VERSION, owner: null, items: parsed.filter(isValidItem).map(normalizeItem) };
      }

      if (!parsed || !Array.isArray(parsed.items)) return fallback;

      return {
        v: STORAGE_VERSION,
        owner: parsed.owner ? String(parsed.owner) : null,
        items: parsed.items.filter(isValidItem).map(normalizeItem)
      };
    } catch (error) {
      return fallback;
    }
  }

  function writePayload(payload) {
    var items = payload.items.slice(0, config.maxItems);

    try {
      window.localStorage.setItem(
        config.storageKey,
        JSON.stringify({ v: STORAGE_VERSION, owner: payload.owner, items: items })
      );
    } catch (error) {
      /* private mode / quota — the cookie mirror below still carries the list */
    }

    mirrorToCookie(items);

    return items;
  }

  /**
   * Keep a compact, readable copy of the list in a cookie. Cookies are capped
   * at ~4KB, so the newest handles win and the rest are dropped from the
   * mirror only — localStorage keeps the full list.
   */
  function mirrorToCookie(items) {
    var handles = [];
    var length = 0;

    for (var i = 0; i < items.length; i++) {
      var handle = items[i].handle;
      var nextLength = length + handle.length + 1;

      if (nextLength > config.cookieMaxLength) break;

      handles.push(handle);
      length = nextLength;
    }

    writeCookie(config.cookieName, handles.join('~'), config.cookieDays);
    writeCookie(config.countCookieName, String(items.length), config.cookieDays);
  }

  /* ----------------------------------------------------------------------- */
  /* State                                                                   */
  /* ----------------------------------------------------------------------- */

  var state = readPayload();
  var subscribers = [];
  var syncTimer = null;

  function sortedItems(items) {
    return items.slice().sort(function (a, b) {
      return b.addedAt - a.addedAt;
    });
  }

  function indexOfHandle(handle) {
    for (var i = 0; i < state.items.length; i++) {
      if (state.items[i].handle === handle) return i;
    }

    return -1;
  }

  function commit(change) {
    state.items = writePayload(state);

    var detail = {
      items: sortedItems(state.items),
      count: state.items.length,
      handle: change && change.handle ? change.handle : null,
      active: change ? !!change.active : null,
      source: change && change.source ? change.source : 'local'
    };

    subscribers.forEach(function (callback) {
      try {
        callback(detail);
      } catch (error) {
        /* a broken subscriber must not stop the others */
      }
    });

    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: detail }));

    if (typeof window.publish === 'function') {
      window.publish('wishlist:change', detail);
    }

    if (detail.source === 'local') scheduleSync();
  }

  /* ----------------------------------------------------------------------- */
  /* Account sync                                                            */
  /* ----------------------------------------------------------------------- */

  /**
   * Reconcile the stored list with the signed-in customer.
   *
   * guest -> customer : the guest list is claimed and merged with whatever the
   *                     account already holds, so nothing collected before
   *                     logging in is lost.
   * customer -> other : the list belongs to somebody else, start from the new
   *                     account's items.
   * customer -> guest : only cleared when a real sync endpoint exists, because
   *                     without one the local copy is the only copy.
   */
  function reconcileOwner() {
    var previousOwner = state.owner;
    var remoteItems = (config.customerItems || []).filter(isValidItem).map(normalizeItem);
    var changed = false;

    if (ownerId) {
      if (previousOwner && previousOwner !== ownerId) {
        state.items = remoteItems;
        changed = true;
      } else {
        remoteItems.forEach(function (item) {
          if (indexOfHandle(item.handle) === -1) {
            state.items.push(item);
            changed = true;
          }
        });
      }

      if (previousOwner !== ownerId) {
        state.owner = ownerId;
        changed = true;
      }
    } else if (previousOwner && config.syncUrl) {
      state.items = [];
      state.owner = null;
      changed = true;
    }

    if (changed) commit({ source: 'sync' });
  }

  function scheduleSync() {
    if (!ownerId || !config.syncUrl) return;

    clearTimeout(syncTimer);
    syncTimer = setTimeout(pushToAccount, 800);
  }

  function pushToAccount() {
    if (!ownerId || !config.syncUrl) return;

    fetch(config.syncUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customer_id: ownerId, items: sortedItems(state.items) })
    }).catch(function () {
      /* sync is best effort, the local list stays intact either way */
    });
  }

  /* ----------------------------------------------------------------------- */
  /* Public API                                                              */
  /* ----------------------------------------------------------------------- */

  var Wishlist = {
    all: function () {
      return sortedItems(state.items);
    },

    get: function (handle) {
      var index = indexOfHandle(handle);
      return index === -1 ? null : Object.assign({}, state.items[index]);
    },

    has: function (handle) {
      return indexOfHandle(handle) !== -1;
    },

    count: function () {
      return state.items.length;
    },

    add: function (item) {
      if (!isValidItem(item) || this.has(item.handle)) return false;

      state.items.push(normalizeItem(item));

      if (state.items.length > config.maxItems) {
        state.items = sortedItems(state.items).slice(0, config.maxItems);
      }

      commit({ handle: item.handle, active: true });

      return true;
    },

    remove: function (handle) {
      var index = indexOfHandle(handle);
      if (index === -1) return false;

      state.items.splice(index, 1);
      commit({ handle: handle, active: false });

      return true;
    },

    toggle: function (item) {
      if (!isValidItem(item)) return false;

      if (this.has(item.handle)) {
        this.remove(item.handle);
        return false;
      }

      this.add(item);

      return true;
    },

    clear: function () {
      if (!state.items.length) return;

      state.items = [];
      commit({ handle: null, active: false });
    },

    subscribe: function (callback) {
      if (typeof callback !== 'function') return function () {};

      subscribers.push(callback);

      return function unsubscribe() {
        subscribers = subscribers.filter(function (entry) {
          return entry !== callback;
        });
      };
    },

    config: config
  };

  window.Wishlist = Wishlist;

  // Keep tabs in the same browser in step with each other.
  window.addEventListener('storage', function (event) {
    if (event.key !== config.storageKey) return;

    state = readPayload();
    commit({ source: 'storage' });
  });

  /* ----------------------------------------------------------------------- */
  /* Rendering helpers                                                       */
  /* ----------------------------------------------------------------------- */

  function rootUrl() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  }

  /**
   * Fetch one alternate product template per saved item. Rendering server side
   * keeps prices, currency and translations correct with no client-side
   * formatting. A `null` body means the product no longer resolves.
   */
  function fetchProductViews(items, view) {
    var root = rootUrl();

    return Promise.all(
      items.map(function (item) {
        return fetch(root + 'products/' + encodeURIComponent(item.handle) + '?view=' + view)
          .then(function (response) {
            return response.ok ? response.text() : null;
          })
          .then(function (html) {
            return { handle: item.handle, html: html };
          })
          .catch(function () {
            return { handle: item.handle, html: null };
          });
      })
    );
  }

  function getDrawer() {
    return document.querySelector('wishlist-drawer');
  }

  /* ----------------------------------------------------------------------- */
  /* Toast                                                                   */
  /* ----------------------------------------------------------------------- */

  var toastTimer = null;

  function showToast(message, withLink) {
    if (!config.showToast) return;

    var stack = document.querySelector('.wishlist-toast');

    if (!stack) {
      stack = document.createElement('div');
      stack.className = 'wishlist-toast';
      stack.setAttribute('role', 'status');
      stack.setAttribute('aria-live', 'polite');
      document.body.appendChild(stack);
    }

    stack.textContent = '';

    var text = document.createElement('span');
    text.className = 'wishlist-toast__text';
    text.textContent = message;
    stack.appendChild(text);

    var drawer = getDrawer();

    if (withLink && (drawer || config.url)) {
      var link = document.createElement('a');
      link.className = 'wishlist-toast__link';
      link.href = config.url || '#';
      link.textContent = strings.view;

      // Prefer opening the drawer in place over sending the visitor away.
      if (drawer) {
        link.addEventListener('click', function (event) {
          event.preventDefault();
          drawer.open(link);
        });
      }

      stack.appendChild(link);
    }

    stack.classList.add('is-visible');

    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      stack.classList.remove('is-visible');
    }, 3200);
  }

  /* ----------------------------------------------------------------------- */
  /* <wishlist-button>                                                       */
  /* ----------------------------------------------------------------------- */

  if (!customElements.get('wishlist-button')) {
    class WishlistButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;

        this.handle = this.dataset.handle;
        if (!this.handle) return;

        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);

        this.unsubscribe = Wishlist.subscribe(this.render.bind(this));
        this.render();
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onClick);
        if (this.unsubscribe) this.unsubscribe();
      }

      onClick(event) {
        // Product cards wrap their media in a link — never follow it from here.
        event.preventDefault();
        event.stopPropagation();

        var active = Wishlist.toggle({
          handle: this.handle,
          productId: this.dataset.productId,
          variantId: this.dataset.variantId
        });

        this.button.classList.add('is-animating');
        setTimeout(
          function () {
            this.button.classList.remove('is-animating');
          }.bind(this),
          450
        );

        showToast(active ? strings.added : strings.removed, active);
      }

      /** Reflects the shared state — every button for this product stays in sync. */
      render() {
        var active = Wishlist.has(this.handle);
        var label = active ? strings.remove : strings.add;

        this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
        this.button.setAttribute('aria-label', label);
        this.button.setAttribute('title', label);
        this.classList.toggle('is-active', active);
      }
    }

    customElements.define('wishlist-button', WishlistButton);
  }

  /* ----------------------------------------------------------------------- */
  /* <wishlist-count>                                                        */
  /* ----------------------------------------------------------------------- */

  if (!customElements.get('wishlist-count')) {
    class WishlistCount extends HTMLElement {
      connectedCallback() {
        this.badge = this.querySelector('[data-wishlist-count-value]') || this;
        this.unsubscribe = Wishlist.subscribe(this.render.bind(this));
        this.render();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      render() {
        var count = Wishlist.count();

        this.badge.textContent = count > 99 ? '+' : count === 0 ? '' : String(count);
        this.badge.classList.toggle('hidden', count === 0);
        this.classList.toggle('is-empty', count === 0);
      }
    }

    customElements.define('wishlist-count', WishlistCount);
  }

  /* ----------------------------------------------------------------------- */
  /* <wishlist-list> — the wishlist page                                     */
  /* ----------------------------------------------------------------------- */

  if (!customElements.get('wishlist-list')) {
    class WishlistList extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-wishlist-grid]');
        this.empty = this.querySelector('[data-wishlist-empty]');
        this.loading = this.querySelector('[data-wishlist-loading]');
        this.clearButton = this.querySelector('[data-wishlist-clear]');
        this.countLabel = this.querySelector('[data-wishlist-count-label]');

        this.rendered = [];

        if (this.clearButton) {
          this.onClear = this.onClear.bind(this);
          this.clearButton.addEventListener('click', this.onClear);
        }

        this.unsubscribe = Wishlist.subscribe(this.onChange.bind(this));
        this.render();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        if (this.clearButton && this.onClear) this.clearButton.removeEventListener('click', this.onClear);
      }

      onClear(event) {
        event.preventDefault();
        Wishlist.clear();
      }

      /**
       * Removals are handled in place so the grid does not flash; anything else
       * (an addition from another tab, a clear) triggers a full re-render.
       */
      onChange(detail) {
        if (detail.handle && detail.active === false) {
          if (this.rendered.indexOf(detail.handle) !== -1) {
            this.removeCard(detail.handle);
          } else {
            // A stale handle dropped during render — nothing on screen to pull out.
            this.updateChrome();
          }

          return;
        }

        this.render();
      }

      removeCard(handle) {
        var card = this.grid && this.grid.querySelector('[data-wishlist-card="' + CSS.escape(handle) + '"]');

        this.rendered = this.rendered.filter(function (entry) {
          return entry !== handle;
        });

        if (!card) {
          this.updateChrome();
          return;
        }

        card.classList.add('is-removing');

        setTimeout(
          function () {
            card.remove();
            this.updateChrome();
          }.bind(this),
          260
        );
      }

      updateChrome() {
        var count = Wishlist.count();

        if (this.empty) this.empty.classList.toggle('hidden', count > 0);
        if (this.grid) this.grid.classList.toggle('hidden', count === 0);
        if (this.clearButton) this.clearButton.classList.toggle('hidden', count === 0);

        if (this.countLabel) {
          var template = this.countLabel.dataset.template || '';
          this.countLabel.textContent = template.replace('[count]', String(count));
          this.countLabel.classList.toggle('hidden', count === 0);
        }
      }

      async render() {
        var items = Wishlist.all();

        this.updateChrome();

        if (!this.grid) return;

        // Only the newest render may write to the grid — an earlier, slower
        // batch of fetches must not overwrite it when it lands late.
        this.renderToken = (this.renderToken || 0) + 1;
        var token = this.renderToken;

        if (!items.length) {
          this.grid.textContent = '';
          this.rendered = [];
          if (this.loading) this.loading.classList.add('hidden');
          return;
        }

        if (this.loading) this.loading.classList.remove('hidden');

        var cards = await fetchProductViews(items, 'card');

        if (token !== this.renderToken) return;

        var fragment = document.createDocumentFragment();
        var rendered = [];
        var stale = [];

        cards.forEach(function (card) {
          // A product that no longer resolves has been deleted or unpublished.
          if (!card.html) {
            stale.push(card.handle);
            return;
          }

          var wrapper = document.createElement('div');
          wrapper.className = 'wishlist__item';
          wrapper.setAttribute('data-wishlist-card', card.handle);
          wrapper.innerHTML = card.html;

          if (!wrapper.querySelector('product-card')) {
            stale.push(card.handle);
            return;
          }

          rendered.push(card.handle);
          fragment.appendChild(wrapper);
        });

        this.grid.textContent = '';
        this.grid.appendChild(fragment);
        this.rendered = rendered;

        if (this.loading) this.loading.classList.add('hidden');

        // The quick-add drawer watches the body for new `.quick-cart-drawer__trigger`
        // nodes, so the injected cards wire themselves up — nothing to do here.

        stale.forEach(function (handle) {
          Wishlist.remove(handle);
        });

        if (!stale.length) this.updateChrome();
      }
    }

    customElements.define('wishlist-list', WishlistList);
  }

  /* ----------------------------------------------------------------------- */
  /* <wishlist-drawer> — slide-in panel opened from the header               */
  /* ----------------------------------------------------------------------- */

  if (!customElements.get('wishlist-drawer')) {
    class WishlistDrawer extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('[data-wishlist-panel]');
        this.itemsEl = this.querySelector('[data-wishlist-items]');
        this.emptyEl = this.querySelector('[data-wishlist-empty]');
        this.loadingEl = this.querySelector('[data-wishlist-loading]');
        this.footEl = this.querySelector('[data-wishlist-foot]');

        this.rendered = [];
        this.isOpen = false;

        this.onKeyup = this.onKeyup.bind(this);
        this.onOutsideClick = this.onOutsideClick.bind(this);
        this.onBodyClick = this.onBodyClick.bind(this);

        this.addEventListener('keyup', this.onKeyup);
        this.addEventListener('click', this.onPanelClick.bind(this));

        // The header link keeps a real href for no-JS; here it opens the panel.
        document.querySelectorAll('[data-wishlist-link]').forEach(
          function (link) {
            link.setAttribute('role', 'button');
            link.setAttribute('aria-haspopup', 'dialog');
            link.setAttribute('aria-controls', 'wishlist-drawer');
            link.addEventListener(
              'click',
              function (event) {
                event.preventDefault();
                this.open(link);
              }.bind(this)
            );
          }.bind(this)
        );

        this.unsubscribe = Wishlist.subscribe(this.onChange.bind(this));
        this.updateChrome();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        document.removeEventListener('click', this.onOutsideClick);
        document.removeEventListener('click', this.onBodyClick, true);
      }

      onKeyup(event) {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      }

      onPanelClick(event) {
        if (event.target.closest('[data-wishlist-drawer-close]')) {
          event.preventDefault();
          this.close();
          return;
        }

        var remove = event.target.closest('[data-wishlist-remove]');
        if (remove) {
          event.preventDefault();
          Wishlist.remove(remove.dataset.wishlistRemove);
        }
      }

      onOutsideClick(event) {
        if (this.panel && !this.panel.contains(event.target)) this.close();
      }

      /**
       * Quick add opens a second drawer on top of this one; step aside so the
       * two panels never stack.
       */
      onBodyClick(event) {
        if (event.target.closest('.quick-cart-drawer__trigger')) this.close();
      }

      open(opener) {
        this.activeElement = opener || document.activeElement;
        this.isOpen = true;

        this.render();

        this.classList.add('is-visible');
        document.body.style.overflow = 'hidden';

        if (this.panel) this.panel.focus();

        // Deferred so the click that opened the panel does not close it again.
        setTimeout(
          function () {
            document.addEventListener('click', this.onOutsideClick);
          }.bind(this),
          100
        );

        document.addEventListener('click', this.onBodyClick, true);
      }

      close() {
        if (!this.isOpen) return;

        this.isOpen = false;
        this.classList.remove('is-visible');
        document.body.style.overflow = '';

        document.removeEventListener('click', this.onOutsideClick);
        document.removeEventListener('click', this.onBodyClick, true);

        if (this.activeElement && typeof this.activeElement.focus === 'function') {
          this.activeElement.focus();
        }
      }

      onChange(detail) {
        this.updateChrome();

        if (!this.isOpen) return;

        if (detail.handle && detail.active === false && this.rendered.indexOf(detail.handle) !== -1) {
          this.removeRow(detail.handle);
          return;
        }

        this.render();
      }

      removeRow(handle) {
        var row = this.itemsEl && this.itemsEl.querySelector('[data-wishlist-item="' + CSS.escape(handle) + '"]');

        this.rendered = this.rendered.filter(function (entry) {
          return entry !== handle;
        });

        if (!row) return;

        row.classList.add('is-removing');

        setTimeout(function () {
          row.remove();
        }, 260);
      }

      updateChrome() {
        var count = Wishlist.count();

        if (this.emptyEl) this.emptyEl.classList.toggle('hidden', count > 0);
        if (this.footEl) this.footEl.classList.toggle('hidden', count === 0);
      }

      async render() {
        var items = Wishlist.all();

        this.updateChrome();

        if (!this.itemsEl) return;

        this.renderToken = (this.renderToken || 0) + 1;
        var token = this.renderToken;

        if (!items.length) {
          this.itemsEl.textContent = '';
          this.rendered = [];
          if (this.loadingEl) this.loadingEl.classList.add('hidden');
          return;
        }

        // Re-opening with an unchanged list should not blank the rows out.
        var unchanged =
          this.rendered.length === items.length &&
          items.every(
            function (item, index) {
              return this.rendered[index] === item.handle;
            }.bind(this)
          );

        if (unchanged) {
          if (this.loadingEl) this.loadingEl.classList.add('hidden');
          return;
        }

        if (this.loadingEl) this.loadingEl.classList.remove('hidden');

        var rows = await fetchProductViews(items, 'wishlist-item');

        if (token !== this.renderToken) return;

        var fragment = document.createDocumentFragment();
        var rendered = [];
        var stale = [];

        rows.forEach(function (row) {
          if (!row.html) {
            stale.push(row.handle);
            return;
          }

          var wrapper = document.createElement('div');
          wrapper.innerHTML = row.html;

          var node = wrapper.querySelector('[data-wishlist-item]');

          if (!node) {
            stale.push(row.handle);
            return;
          }

          rendered.push(row.handle);
          fragment.appendChild(node);
        });

        this.itemsEl.textContent = '';
        this.itemsEl.appendChild(fragment);
        this.rendered = rendered;

        if (this.loadingEl) this.loadingEl.classList.add('hidden');

        stale.forEach(function (handle) {
          Wishlist.remove(handle);
        });
      }
    }

    customElements.define('wishlist-drawer', WishlistDrawer);
  }

  /* ----------------------------------------------------------------------- */
  /* Boot                                                                    */
  /* ----------------------------------------------------------------------- */

  reconcileOwner();
  mirrorToCookie(state.items);
})();
